import type { ThoughtStep } from '@/utils/aiThought';

export type SorAnalyzeStepStatus = {
  key: string;
  label: string;
  status: 'pending' | 'processing' | 'done';
};

const STEP_TITLES: Record<string, string> = {
  pipeline_start: '启动解析管线',
  step1_convert: '文档解析',
  step2_chunk: '文档切片',
  step3_collect: '需求提取',
  step3_write: '需求生成',
  step4_design: '联合生成需求与规格',
  step4_write: '设计规格写入',
  step5_test: '规格校验与案例草稿',
  step5_write: '测试规格写入',
  merge: '合并结果',
  nested_locate: '引用定位',
  risk_assessment: '风险评估',
  completed: '解析完成',
  extract_text: '提取文档文本',
  llm_analyze: '大模型解析',
  submit: '任务提交',
  processing: '处理中',
};

const SOR_STEP_DISPLAY_ORDER = [
  'pipeline_start',
  'submit',
  'step1_convert',
  'step2_chunk',
  'step3_collect',
  'step3_write',
  'nested_locate',
  'step4_design',
  'step4_write',
  'step5_test',
  'step5_write',
  'merge',
  'risk_assessment',
  'completed',
  'extract_text',
  'llm_analyze',
  'processing',
];

export function sortSorAnalyzeSteps(steps: SorAnalyzeStepStatus[]): SorAnalyzeStepStatus[] {
  return [...steps].sort((a, b) => {
    const ia = SOR_STEP_DISPLAY_ORDER.indexOf(a.key);
    const ib = SOR_STEP_DISPLAY_ORDER.indexOf(b.key);
    return (ia >= 0 ? ia : 99) - (ib >= 0 ? ib : 99);
  });
}

/** 展示用步骤文案规范化 */
export function normalizeSorAnalyzeStepLabel(key: string, label: string, status: SorAnalyzeStepStatus['status']): string {
  if (key === 'step1_convert') {
    if (status === 'done') return '解析完成';
    if (/完成/.test(label)) return '解析完成';
    return '正在解析文档';
  }
  if (key === 'step3_collect') {
    const splitMatch = label.match(/拆分成\s*(\d+)\s*个需求子项/);
    if (splitMatch) return `拆分成 ${splitMatch[1]} 个需求子项`;
    if (status === 'done' && /拆分成/.test(label)) return label;
    if (/正在提取|提取中/.test(label)) return label;
  }
  if (key === 'step3_write' || key === 'step4_write' || key === 'step5_write') {
    return label;
  }
  return label;
}

export function sorAnalyzeStepTitle(key: string): string {
  return STEP_TITLES[key] || key;
}

export function mapSorAnalyzeStepsToThought(steps: SorAnalyzeStepStatus[]): ThoughtStep[] {
  if (!steps.length) {
    return [
      {
        key: 'sor_waiting',
        icon: '⏳',
        title: '准备中',
        text: '正在启动 SOR 需求解析…',
      },
    ];
  }
  return sortSorAnalyzeSteps(steps).map((s) => ({
    key: s.key,
    icon: s.status === 'done' ? '✅' : s.status === 'processing' ? '⏳' : '○',
    title: sorAnalyzeStepTitle(s.key),
    text: normalizeSorAnalyzeStepLabel(s.key, s.label, s.status),
  }));
}
